import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Home, Users, BarChart3, Mail, LogOut, Trophy, CalendarClock, Send, Loader2, MessageCircle, Ban, ShieldAlert } from "lucide-react";
import { useLang } from "@/i18n";
import { SLOT_LABELS } from "../admin/AdminTeams";
import { errMsg } from "../admin/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function b64ToUint8(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function subscribePush() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return;
    const { data } = await axios.get(`${API}/portail/push/vapid-key`);
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.publicKey) });
    await axios.post(`${API}/portail/push/subscribe`, { subscription: sub.toJSON() });
  } catch {
    /* push indisponible (iOS non installé, refus…) — les emails restent actifs */
  }
}

function MatchCard({ m, t }) {
  const played = m.statut === "joue" && m.score_a != null;
  return (
    <div className="border border-[#27272A] bg-[#121214] px-4 py-3.5 flex items-center gap-3" data-testid={`portail-match-${m.id.slice(0, 8)}`}>
      <div className="text-[#C5A059] text-xs font-medium whitespace-nowrap w-[86px]">
        <p>{new Date(`${m.date}T00:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}</p>
        <p>{m.heure}</p>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{m.equipe_a} vs {m.equipe_b}</p>
        <p className="text-[#71717A] text-[10px] uppercase tracking-wider">{m.label}</p>
      </div>
      <p className={`font-display font-bold text-lg ${played ? "text-[#C5A059]" : "text-[#3f3f46]"}`}>
        {played ? `${m.score_a} - ${m.score_b}` : "—"}
      </p>
    </div>
  );
}

export default function PortailHome() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [teamData, setTeamData] = useState({ team: null, matchs: [] });
  const [sujets, setSujets] = useState([]);
  const [tab, setTab] = useState("accueil");
  const [sujet, setSujet] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [mesMessages, setMesMessages] = useState([]);
  const [chat, setChat] = useState({ team: null, messages: [], bloque: false });
  const [chatInput, setChatInput] = useState("");
  const [chatSending, setChatSending] = useState(false);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem("d2r_player")}` }), []);

  const loadChat = useCallback(() => {
    axios.get(`${API}/portail/equipe/chat`, { headers: authHeaders() }).then((r) => setChat(r.data)).catch(() => {});
  }, [authHeaders]);

  const loadMessages = useCallback(() => {
    axios.get(`${API}/portail/messages`, { headers: authHeaders() }).then((r) => setMesMessages(r.data)).catch(() => {});
  }, [authHeaders]);

  useEffect(() => {
    if (!localStorage.getItem("d2r_player")) {
      navigate("/portail");
      return;
    }
    axios.get(`${API}/portail/me`, { headers: authHeaders(), withCredentials: true })
      .then((r) => setPlayer(r.data))
      .catch(() => { localStorage.removeItem("d2r_player"); navigate("/portail"); });
    axios.get(`${API}/portail/equipe`, { headers: authHeaders() }).then((r) => setTeamData(r.data)).catch(() => {});
    axios.get(`${API}/portail/sujets`).then((r) => { setSujets(r.data); setSujet(r.data[0] || ""); }).catch(() => {});
    loadMessages();
    subscribePush();
  }, [navigate, authHeaders, loadMessages]);

  // Rafraîchit le chat d'équipe toutes les 15 s quand l'onglet est ouvert
  useEffect(() => {
    if (tab !== "chat") return undefined;
    loadChat();
    const iv = setInterval(loadChat, 15000);
    return () => clearInterval(iv);
  }, [tab, loadChat]);

  if (!player) {
    return <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center"><p className="text-[#71717A] text-xs uppercase tracking-[0.3em]">Chargement…</p></div>;
  }

  const heure = new Date().getHours();
  const salutation = heure >= 18 || heure < 5 ? t("portal.hi.evening") : t("portal.hi.morning");
  const { team, matchs } = teamData;
  const today = new Date().toISOString().slice(0, 10);
  const prochain = matchs.find((m) => m.date >= today && m.statut === "a_venir");
  const s = player.stats || {};
  const hasVeo = ["distance_km", "vitesse_max", "sprints", "passes_pct", "tirs_cadres", "duels"].some((k) => s[k] != null && s[k] !== "");

  const logout = () => {
    localStorage.removeItem("d2r_player");
    navigate("/portail");
  };

  const envoyerMessage = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/portail/messages`, { sujet, message }, { headers: authHeaders() });
      setSent(true);
      setMessage("");
      loadMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  const NAV = [
    ["accueil", t("portal.nav.home"), Home],
    ["equipe", t("portal.nav.team"), Users],
    ["chat", "Chat", MessageCircle],
    ["stats", t("portal.nav.stats"), BarChart3],
    ["contact", t("portal.nav.contact"), Mail],
  ];

  const envoyerChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    setChatSending(true);
    try {
      await axios.post(`${API}/portail/equipe/chat`, { contenu: chatInput }, { headers: authHeaders() });
      setChatInput("");
      loadChat();
    } catch (err) {
      toast.error(errMsg(err, "Erreur d'envoi"));
    } finally {
      setChatSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white pb-24" data-testid="portail-home">
      {/* En-tête édition */}
      <header className="sticky top-0 z-20 bg-[#0A0A0C]/95 backdrop-blur border-b border-[#27272A]">
        <div className="max-w-md mx-auto px-5 py-4 flex items-center gap-3">
          <img src="/assets/logo-dark-sm.png" alt="D2R" className="h-9 w-auto" />
          <div className="flex-1 min-w-0">
            <p className="font-display uppercase font-bold text-lg leading-tight truncate" data-testid="portail-greeting">
              {salutation}, <span className="text-[#C5A059]">{player.prenom}</span>
            </p>
            <p className="text-[#71717A] text-[9px] uppercase tracking-[0.3em]">{t("portal.edition")}</p>
          </div>
          <button onClick={logout} data-testid="portail-logout" title={t("portal.logout")} className="text-[#71717A] hover:text-red-400 transition-colors p-2">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className="max-w-md mx-auto px-5 pt-6">
        {tab === "accueil" && (
          <div className="space-y-5">
            <div className="border border-[#C5A059]/40 bg-[#121214] p-5" data-testid="portail-ref-card">
              <p className="text-[9px] uppercase tracking-[0.3em] text-[#71717A]">{t("portal.home.ref")}</p>
              <p className="font-display text-2xl font-bold text-[#C5A059] mt-1">{player.reference}</p>
              <p className="text-[#A1A1AA] text-xs mt-2">{player.poste_principal} · {player.categorie} · {player.genre === "féminin" ? "Féminin" : "Masculin"}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-3">{t("portal.home.next")}</p>
              {prochain ? <MatchCard m={prochain} t={t} /> : (
                <p className="border border-[#27272A] bg-[#121214] p-5 text-sm text-[#71717A]" data-testid="portail-no-match">{t("portal.home.none")}</p>
              )}
            </div>
            <p className="text-[#3f3f46] text-[11px] leading-relaxed border-t border-[#27272A] pt-4">{t("portal.home.install")}</p>
          </div>
        )}

        {tab === "equipe" && (
          <div className="space-y-5" data-testid="portail-equipe">
            <h2 className="font-display text-xl uppercase font-bold">{t("portal.team.title")}</h2>
            {!team ? (
              <p className="border border-[#27272A] bg-[#121214] p-5 text-sm text-[#71717A]">{t("portal.team.none")}</p>
            ) : (
              <>
                <div className="border border-[#27272A] bg-[#121214] p-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display text-lg uppercase font-bold">{team.nom}</p>
                    <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 border border-[#C5A059]/40 text-[#C5A059]">{team.categorie || "U23"}</span>
                  </div>
                  <p className="text-[#A1A1AA] text-xs mt-2">{t("portal.team.coach")} : {team.coach || "—"} · {t("portal.team.slot")} : {team.creneau || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-3">{t("portal.team.players")} ({team.joueurs.length}/11)</p>
                  <div className="border border-[#27272A] bg-[#121214] divide-y divide-[#27272A]/60">
                    {team.joueurs.map((j) => (
                      <div key={j.registration_id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className={j.registration_id === player.id ? "text-[#C5A059] font-semibold" : "text-white"}>{j.prenom} {j.nom}</span>
                        <span className="text-[#71717A] text-[10px] uppercase tracking-wider">{SLOT_LABELS[j.slot]}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-3 flex items-center gap-2"><CalendarClock size={12} className="text-[#C5A059]" /> {t("portal.team.matchs")}</p>
                  <div className="space-y-2">
                    {matchs.length === 0 && <p className="border border-[#27272A] bg-[#121214] p-5 text-sm text-[#71717A]">{t("portal.home.none")}</p>}
                    {matchs.map((m) => <MatchCard key={m.id} m={m} t={t} />)}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "chat" && (
          <div className="space-y-4" data-testid="portail-chat">
            <h2 className="font-display text-xl uppercase font-bold">Chat d'équipe{chat.team ? ` — ${chat.team.nom}` : ""}</h2>
            {!chat.team ? (
              <p className="border border-[#27272A] bg-[#121214] p-5 text-sm text-[#71717A]" data-testid="portail-chat-none">
                Vous serez ajouté au chat dès que vous serez placé dans une équipe.
              </p>
            ) : (
              <>
                {chat.bloque && (
                  <p className="border border-red-400/50 bg-red-400/10 p-4 text-xs text-red-300 flex items-center gap-2" data-testid="portail-chat-bloque">
                    <Ban size={14} /> Vous avez été bloqué du chat par le staff. Vous pouvez lire les messages mais plus écrire.
                  </p>
                )}
                <div className="space-y-2.5" data-testid="portail-chat-messages">
                  {chat.messages.length === 0 && (
                    <p className="border border-[#27272A] bg-[#121214] p-5 text-sm text-[#71717A]">Aucun message pour le moment — lancez la conversation !</p>
                  )}
                  {chat.messages.map((m) => {
                    const isMe = m.auteur_id === player.id;
                    if (m.type === "avertissement") {
                      return (
                        <div key={m.id} data-testid={`chat-msg-${m.id.slice(0, 8)}`} className="border border-red-400/50 bg-red-400/5 p-3.5">
                          <p className="text-[9px] uppercase tracking-widest text-red-300 flex items-center gap-1.5">
                            <ShieldAlert size={11} /> Avertissement du staff{m.cible_nom ? ` pour ${m.cible_nom}` : ""}
                          </p>
                          <p className="text-sm text-white mt-1.5 whitespace-pre-wrap">{m.contenu}</p>
                          <p className="text-[#3f3f46] text-[10px] mt-1">{new Date(m.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                        </div>
                      );
                    }
                    return (
                      <div key={m.id} data-testid={`chat-msg-${m.id.slice(0, 8)}`}
                        className={`max-w-[85%] p-3.5 border ${isMe ? "ml-auto border-[#C5A059]/50 bg-[#C5A059]/10" : m.auteur_type === "admin" ? "border-[#C5A059]/40 bg-[#121214]" : "border-[#27272A] bg-[#121214]"}`}>
                        <p className={`text-[9px] uppercase tracking-widest ${m.auteur_type === "admin" ? "text-[#C5A059]" : "text-[#71717A]"}`}>
                          {m.auteur_type === "admin" ? "Staff D2R" : isMe ? "Moi" : m.auteur_nom}
                        </p>
                        <p className="text-sm text-white mt-1 whitespace-pre-wrap">{m.contenu}</p>
                        <p className="text-[#3f3f46] text-[10px] mt-1">{new Date(m.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                      </div>
                    );
                  })}
                </div>
                {!chat.bloque && (
                  <form onSubmit={envoyerChat} className="flex gap-2">
                    <input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      maxLength={500}
                      placeholder="Écrire à votre équipe…"
                      data-testid="portail-chat-input"
                      className="input-editorial flex-1"
                    />
                    <button type="submit" disabled={chatSending || !chatInput.trim()} data-testid="portail-chat-send"
                      className="flex items-center justify-center bg-[#C5A059] text-black px-4 hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
                      {chatSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </button>
                  </form>
                )}
              </>
            )}
          </div>
        )}

        {tab === "stats" && (
          <div className="space-y-5" data-testid="portail-stats">
            <h2 className="font-display text-xl uppercase font-bold">{t("portal.stats.title")}</h2>
            <div className="grid grid-cols-3 gap-3">
              {[["matchs", s.matchs ?? 0], ["buts", s.buts ?? 0], ["passes", s.passes ?? 0]].map(([k, v]) => (
                <div key={k} className="border border-[#27272A] bg-[#121214] p-4 text-center" data-testid={`portail-stat-${k}`}>
                  <p className="font-display text-3xl font-bold text-[#C5A059]">{v}</p>
                  <p className="text-[9px] uppercase tracking-widest text-[#71717A] mt-1">{t(`portal.stats.${k}`)}</p>
                </div>
              ))}
            </div>
            {hasVeo ? (
              <div className="grid grid-cols-2 gap-3" data-testid="portail-veo-stats">
                {[["distance", "distance_km"], ["vitesse", "vitesse_max"], ["sprints", "sprints"], ["passes_pct", "passes_pct"], ["tirs", "tirs_cadres"], ["duels", "duels"]].map(([label, key]) => (
                  <div key={key} className="border border-[#27272A] bg-[#121214] p-4">
                    <p className="font-display text-2xl font-bold">{s[key] ?? "—"}</p>
                    <p className="text-[9px] uppercase tracking-widest text-[#71717A] mt-1">{t(`portal.stats.${label}`)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="border border-[#27272A] bg-[#121214] p-5 text-sm text-[#71717A] leading-relaxed">{t("portal.stats.empty")}</p>
            )}
            <div className="flex items-center gap-2 text-[10px] text-[#3f3f46] uppercase tracking-widest"><Trophy size={11} /> Données issues des analyses caméra Veo</div>
          </div>
        )}

        {tab === "contact" && (
          <div className="space-y-5" data-testid="portail-contact">
            <h2 className="font-display text-xl uppercase font-bold">{t("portal.contact.title")}</h2>
            {sent ? (
              <p className="border border-emerald-400/40 bg-emerald-400/5 p-5 text-sm text-emerald-300" data-testid="portail-contact-sent">{t("portal.contact.sent")}</p>
            ) : (
              <form onSubmit={envoyerMessage} className="space-y-5">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{t("portal.contact.subject")}</label>
                  <select value={sujet} onChange={(e) => setSujet(e.target.value)} data-testid="portail-contact-sujet" className="input-editorial mt-1">
                    {sujets.map((s2) => <option key={s2} value={s2}>{s2}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{t("portal.contact.message")}</label>
                  <textarea required rows={5} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="portail-contact-message" className="input-editorial mt-1 resize-none" />
                </div>
                <button type="submit" disabled={sending} data-testid="portail-contact-send" className="w-full flex items-center justify-center gap-2 bg-[#C5A059] text-black py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                  {t("portal.contact.send")}
                </button>
              </form>
            )}
            {mesMessages.length > 0 && (
              <div className="space-y-3 pt-2" data-testid="portail-messages-thread">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("portal.contact.thread")}</p>
                {[...mesMessages].reverse().map((m) => (
                  <div key={m.id} className="border border-[#27272A] bg-[#121214] p-4 space-y-2">
                    <p className="text-[#C5A059] text-[10px] uppercase tracking-wider">{m.sujet}</p>
                    <p className="text-sm text-white whitespace-pre-wrap">{m.message}</p>
                    <p className="text-[#3f3f46] text-[10px]">{new Date(m.created_at).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
                    {(m.reponses || []).map((r, j) => (
                      <div key={j} className="border-l-2 border-[#C5A059] bg-[#0A0A0C] px-3 py-2.5" data-testid={`portail-reponse-${m.id.slice(0, 8)}-${j}`}>
                        <p className="text-[9px] uppercase tracking-widest text-[#C5A059]">{t("portal.contact.staff")}</p>
                        <p className="text-sm text-[#A1A1AA] mt-1 whitespace-pre-wrap">{r.texte}</p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Navigation basse façon app mobile */}
      <nav className="fixed bottom-0 inset-x-0 z-20 bg-[#0A0A0C]/95 backdrop-blur border-t border-[#27272A]" data-testid="portail-nav">
        <div className="max-w-md mx-auto grid grid-cols-5">
          {NAV.map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => { setTab(id); setSent(false); }}
              data-testid={`portail-nav-${id}`}
              className={`flex flex-col items-center gap-1 py-3 text-[9px] uppercase tracking-widest transition-colors ${tab === id ? "text-[#C5A059]" : "text-[#71717A] hover:text-white"}`}
            >
              <Icon size={18} />
              {label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
